import { router } from '@inertiajs/vue3'
import mixpanel from 'mixpanel-browser'
import type { App } from 'vue'

export interface MixpanelConfig {
    token: string
    apiHost?: string
    debug?: boolean
    trackPageviews?: boolean
    persistence?: string
    isImpersonating?: boolean
}

let mixpanelInstance: typeof mixpanel | null = null
let impersonationActive = false

export function initMixpanel(config: MixpanelConfig) {
    impersonationActive = config.isImpersonating ?? false

    // Impersonated sessions must capture NOTHING to Mixpanel — same policy
    // and same person-merge hazard as PostHog (see lib/posthog.ts): an
    // identify() during impersonation would merge the staff browser's device
    // history into the customer's person record. Unlike posthog-js there is
    // no autocapture or session replay here, so the init gate plus the
    // per-call guards below cover every send this module can make; the
    // navigate-time sync handles a shared session flipping in another tab.
    if (impersonationActive) {
        return null
    }

    if (!config.token) {
        console.warn('Mixpanel token not provided, skipping initialization')
        return null
    }

    // Initialize Mixpanel with configuration
    mixpanel.init(config.token, {
        api_host: config.apiHost || 'https://api-eu.mixpanel.com',
        debug: config.debug ?? false,
        track_pageview: config.trackPageviews ?? false,
        persistence: (config.persistence ?? 'localStorage') as 'localStorage',
        loaded: mp => {
            // Identify if user is available
            const windowWithInertia = window as any
            const admin = windowWithInertia.Inertia?.page?.props?.admin

            // Skip identification when staff is impersonating
            if (admin?.is_impersonating) {
                return
            }

            const user = windowWithInertia.Inertia?.page?.props?.user
            const workspace =
                windowWithInertia.Inertia?.page?.props?.workspace ||
                windowWithInertia.Inertia?.page?.props?.current_website

            if (user?.id) {
                // Identify user
                mp.identify(user.id.toString())

                // Set user properties
                mp.people.set({
                    $email: user.email,
                    $name: user.name,
                    $created: user.created_at,
                    is_admin: user.is_admin || false,
                    has_2fa_enabled: user.two_factor_enabled || false,
                })

                // Set workspace group if available
                if (workspace?.workspace_uuid || workspace?.uuid) {
                    const workspaceUuid =
                        workspace.workspace_uuid || workspace.uuid
                    // Add user to workspace group
                    mp.set_group('workspace_id', workspaceUuid)

                    // Set group properties
                    mp.get_group('workspace_id', workspaceUuid).set({
                        name: workspace.name,
                        domain: workspace.domain || workspace.url,
                        plan: workspace.plan,
                        created_at: workspace.created_at,
                        trial_ends_at: workspace.trial_ends_at,
                        tracker_installed:
                            workspace.last_data_received_at !== null,
                    })
                }
            }
        },
    })

    mixpanelInstance = mixpanel

    // Set up Inertia navigation tracking
    setupInertiaTracking()

    return mixpanel
}

function setupInertiaTracking() {
    if (!mixpanelInstance) return

    // Track page views on Inertia navigation
    router.on('navigate', event => {
        const page = event.detail.page

        // Skip tracking when staff is impersonating
        const admin = page.props.admin as
            | { is_impersonating?: boolean }
            | undefined
        impersonationActive = admin?.is_impersonating ?? false
        if (impersonationActive) {
            return
        }

        const url = window.location.href
        const path = window.location.pathname

        // Track page view
        mixpanelInstance.track('Page Viewed', {
            url: url,
            path: path,
            component: page.component,
            title: document.title,
        })

        // Update user identification if user data changes
        const user = page.props.user as any
        const workspace = (page.props.workspace ||
            page.props.current_website) as any

        if (user?.id) {
            mixpanelInstance.identify(user.id.toString())

            // Update user properties
            mixpanelInstance.people.set({
                $email: user.email,
                $name: user.name,
                $created: user.created_at,
                is_admin: user.is_admin || false,
                has_2fa_enabled: user.two_factor_enabled || false,
            })

            // Update workspace group if changed
            if (workspace?.workspace_uuid || workspace?.uuid) {
                const workspaceUuid = workspace.workspace_uuid || workspace.uuid
                // Set the current group
                mixpanelInstance.set_group('workspace_id', workspaceUuid)

                // Update group properties
                mixpanelInstance.get_group('workspace_id', workspaceUuid).set({
                    name: workspace.name,
                    domain: workspace.domain || workspace.url,
                    plan: workspace.plan,
                    created_at: workspace.created_at,
                    trial_ends_at: workspace.trial_ends_at,
                    tracker_installed: workspace.last_data_received_at !== null,
                })
            }
        }

        // Track specific page views
        trackSpecificPageViews(path, page.component)
    })

    // Track onboarding completion
    router.on('success', event => {
        if (impersonationActive) {
            return
        }
        const path = window.location.pathname
        if (path === '/onboarding/success') {
            mixpanelInstance.track('Onboarding Completed')
        }
    })
}

function trackSpecificPageViews(path: string, component: string) {
    // Track onboarding steps
    if (path.includes('/onboarding/')) {
        const step = path.split('/').pop()
        mixpanelInstance?.track('Onboarding Step Viewed', {
            step: step,
            component: component,
        })
    }

    // Track feature page views
    const featurePages: Record<string, string> = {
        '/dashboard': 'dashboard',
        '/leads': 'leads',
        '/segments': 'segments',
        '/automations': 'automations',
        '/reports': 'reports',
        '/integrations': 'integrations',
        '/settings': 'settings',
        '/team': 'team_management',
        '/billing': 'billing',
    }

    for (const [pagePath, feature] of Object.entries(featurePages)) {
        if (path.startsWith(pagePath)) {
            mixpanelInstance?.track('Feature Viewed', {
                feature: feature,
                full_path: path,
            })
            break
        }
    }
}

// Export functions for manual event tracking
export function trackEvent(
    eventName: string,
    properties?: Record<string, any>
) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }

    // Events are automatically associated with the current group
    mixpanelInstance.track(eventName, properties)
}

export function identifyUser(userId: string, properties?: Record<string, any>) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }
    mixpanelInstance.identify(userId)
    if (properties) {
        mixpanelInstance.people.set(properties)
    }
}

export function setUserProperties(properties: Record<string, any>) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }
    mixpanelInstance.people.set(properties)
}

export function setUserGroup(
    groupKey: string,
    groupId: string,
    properties?: Record<string, any>
) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }

    // Set the user's group
    mixpanelInstance.set_group(groupKey, groupId)

    // Set group properties if provided
    if (properties) {
        mixpanelInstance.get_group(groupKey, groupId).set(properties)
    }
}

export function updateGroupProperties(
    groupKey: string,
    groupId: string,
    properties: Record<string, any>
) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }

    mixpanelInstance.get_group(groupKey, groupId).set(properties)
}

export function incrementUserProperty(property: string, increment: number = 1) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }
    mixpanelInstance.people.increment(property, increment)
}

export function incrementGroupProperty(
    groupKey: string,
    groupId: string,
    property: string,
    increment: number = 1
) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }

    mixpanelInstance.get_group(groupKey, groupId).set_once({
        [property]: 0,
    })
    mixpanelInstance.get_group(groupKey, groupId).set(
        {
            [property]: increment,
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        { $add: true } as any
    )
}

export function trackOnboardingProgress(
    step: string,
    data?: Record<string, any>
) {
    trackEvent('Onboarding Progress', {
        step,
        ...data,
    })
}

export function trackFeatureUsage(
    feature: string,
    action: string,
    properties?: Record<string, any>
) {
    trackEvent('Feature Used', {
        feature,
        action,
        ...properties,
    })
}

export function trackRevenue(amount: number, properties?: Record<string, any>) {
    if (impersonationActive) {
        return
    }
    if (!mixpanelInstance) {
        console.warn('Mixpanel not initialized')
        return
    }
    mixpanelInstance.people.track_charge(amount, properties)
}

// Vue plugin
export const MixpanelPlugin = {
    install(app: App, options: MixpanelConfig) {
        const instance = initMixpanel(options)

        if (instance) {
            app.config.globalProperties.$mixpanel = instance
            app.provide('mixpanel', instance)
        }
    },
}

// Composable for using Mixpanel in components
export function useMixpanel() {
    return {
        trackEvent,
        identifyUser,
        setUserProperties,
        setUserGroup,
        updateGroupProperties,
        incrementUserProperty,
        incrementGroupProperty,
        trackOnboardingProgress,
        trackFeatureUsage,
        trackRevenue,
        mixpanel: mixpanelInstance,
    }
}
